// Console sessions on a staff-run station (ADR-0034; spec/13 §3.3): how a console is sold in an Indian café — a block
// of time for a number of controllers, paid once at the counter when it ends.
//
//   Start      player (or a walk-in), controllers 1 … the station's seats, time 30 min / 1 h / 2 h / 5 h / Custom /
//              Open, an optional game, and the price — lines per controller, the console offers, "Due at the end".
//   Add time   one more block, with what it adds to what is due.
//   Controllers  from this moment on, with what it changes.
//   Due        the running session's booked blocks, controllers and what is due at the end.
//
// The console never prices a session: every figure is the server's `quoteConsoleSession` (`useConsoleQuote`,
// `useSessionChangeQuote`, `Session.booking.quote`), the same function that prices the order placed at the end.

import type { ConsoleQuote, FloorStationState, OrganizationPlayer, Session } from '@clavian/api-client';
import { CONSOLE_BLOCK_MINUTES, CONSOLE_MAX_CONTROLLERS } from '@clavian/contracts/constants';
import { useTranslation } from '@clavian/i18n/react';
import { Button, Checkbox, SelectField, Skeleton, TextField, useToast } from '@clavian/ui-web';
import { useQuery } from '@tanstack/react-query';
import { useDeferredValue, useState, type FormEvent, type ReactNode } from 'react';

import { useApi } from '../../api/client';
import { PROBLEM, problemCode } from '../../api/problems';
import { useProblem } from '../../api/use-problem';
import { Notice } from '../../ui';
import { playerName } from '../players/queries';

import { formatDuration, formatMoney } from './money';
import { PlayerPicker } from './PlayerPicker';
import { useAddSessionTime, useConsoleQuote, useSessionChangeQuote, useSetSessionControllers, useStartSession } from './queries';

/** A time chip: a block of minutes, a custom length, or Open (no end). */
type TimeChoice = `${(typeof CONSOLE_BLOCK_MINUTES)[number]}` | 'custom' | 'open';

/** "30 min", "1 h", "2 h 30 min" — a block's length. */
export function blockText(minutes: number, t: (key: string, options?: Record<string, unknown>) => string): string {
  const hours = Math.floor(minutes / 60);
  const rest = minutes % 60;
  if (hours === 0) return t('consoleSession.minutes', { n: rest });
  return rest === 0 ? t('consoleSession.hours', { n: hours }) : t('consoleSession.hoursMinutes', { h: hours, m: rest });
}

/** The controllers a station takes: its seats, at least one, at most the contract's cap. */
export function controllerChoices(capacity: number): number[] {
  const most = Math.min(CONSOLE_MAX_CONTROLLERS, Math.max(1, capacity));
  return Array.from({ length: most }, (_, index) => index + 1);
}

/** The minutes a time choice books: a chip's, the custom field's (when valid), or null for Open; undefined when invalid. */
function minutesOf(choice: TimeChoice, custom: string): number | null | undefined {
  if (choice === 'open') return null;
  if (choice !== 'custom') return Number(choice);
  const value = Number(custom);
  return custom.trim() !== '' && Number.isInteger(value) && value >= 1 && value <= 1440 ? value : undefined;
}

function Chips<T extends string | number>({
  label,
  options,
  value,
  onChange,
}: {
  readonly label: string;
  readonly options: readonly { readonly value: T; readonly label: string }[];
  readonly value: T;
  readonly onChange: (value: T) => void;
}): ReactNode {
  return (
    <div className="console-booking__field">
      <span className="console-booking__label">{label}</span>
      <div className="console-session__quick" role="group" aria-label={label}>
        {options.map((option) => (
          <button key={String(option.value)} type="button" className="console-session__quick-chip" aria-pressed={value === option.value} onClick={() => onChange(option.value)}>
            {option.label}
          </button>
        ))}
      </div>
    </div>
  );
}

/** The time chips: 30 min, 1 h, 2 h, 5 h, Custom (and Open where it may be booked). */
function timeOptions(withOpen: boolean, t: (key: string, options?: Record<string, unknown>) => string): { value: TimeChoice; label: string }[] {
  return [
    ...CONSOLE_BLOCK_MINUTES.map((minutes) => ({ value: `${minutes}` as TimeChoice, label: blockText(minutes, t) })),
    { value: 'custom' as const, label: t('consoleSession.start.custom') },
    ...(withOpen ? [{ value: 'open' as const, label: t('consoleSession.start.open') }] : []),
  ];
}

/**
 * A quote's lines: each block's first controller (its block price, or the rate for its length) and its extra
 * controllers, what the console offers took off, and the total "Due at the end".
 */
export function QuoteLines({ quote, title, footer }: { readonly quote: ConsoleQuote; readonly title?: string; readonly footer?: ReactNode }): ReactNode {
  const { t } = useTranslation('console');
  const lineLabel = (line: ConsoleQuote['lines'][number]): string => {
    const block = line.blockMinutes === null ? t('consoleSession.preview.openBlock', { time: formatDuration(line.seconds) }) : blockText(line.blockMinutes, t);
    return line.kind === 'first_controller'
      ? t('consoleSession.preview.firstController', { block })
      : t('consoleSession.preview.extraControllers', { block, count: line.quantity, time: blockText(Math.round(line.seconds / 60), t) });
  };
  return (
    <div className="console-booking__quote">
      {title === undefined ? null : <p className="console-booking__label">{title}</p>}
      {quote.lines.length === 0 ? (
        <p className="console-subtle">{quote.open ? t('consoleSession.preview.openRate', { amount: formatMoney(quote.hourlyAmount) }) : t('consoleSession.preview.nothing')}</p>
      ) : (
        <ul className="console-booking__lines">
          {quote.lines.map((line, index) => (
            <li key={`${line.kind}-${line.blockIndex}-${line.fromSecond}-${index}`} className="console-booking__line">
              <span>
                {lineLabel(line)}
                {line.basis === 'block' ? <span className="console-subtle"> · {t('consoleSession.preview.blockPrice')}</span> : null}
              </span>
              <span className="console-money">{formatMoney(line.amount)}</span>
            </li>
          ))}
          {quote.offers.map((offer) => (
            <li key={offer.promotionId} className="console-booking__line console-booking__offer">
              <span>{offer.name}</span>
              <span className="console-money">−{formatMoney(offer.amount)}</span>
            </li>
          ))}
        </ul>
      )}
      <p className="console-booking__total">
        <span>{t('consoleSession.preview.due')}</span>
        <strong className="console-money">{formatMoney(quote.total)}</strong>
      </p>
      {quote.open && quote.lines.length > 0 ? <p className="console-subtle">{t('consoleSession.preview.openRate', { amount: formatMoney(quote.hourlyAmount) })}</p> : null}
      {footer}
    </div>
  );
}

/**
 * Start a console session on a free staff-run station: who (a player, or a walk-in), how many controllers, how long
 * (or Open), what game, and what it will cost — then the countdown starts and the order is placed when it ends.
 */
export function ConsoleStartForm({
  organizationId,
  venueId,
  station,
  pickGame,
  onDone,
}: {
  readonly organizationId: string;
  readonly venueId: string;
  readonly station: Pick<FloorStationState, 'stationId' | 'capacity' | 'number' | 'name'>;
  /** The game picker, for a role that may read the library (ADR-0033). */
  readonly pickGame: boolean;
  readonly onDone: () => void;
}): ReactNode {
  const { t } = useTranslation('console');
  const problem = useProblem();
  const { show } = useToast();
  const api = useApi();
  const start = useStartSession(organizationId, venueId);
  const [player, setPlayer] = useState<OrganizationPlayer | null>(null);
  const [walkIn, setWalkIn] = useState(false);
  const [controllers, setControllers] = useState(1);
  const [time, setTime] = useState<TimeChoice>('60');
  const [custom, setCustom] = useState('');
  const [game, setGame] = useState('');
  const [error, setError] = useState<string | undefined>(undefined);
  const [fieldError, setFieldError] = useState<string | undefined>(undefined);

  const library = useQuery({
    queryKey: ['sessions', 'start-games', organizationId, venueId],
    queryFn: () => api.listLibraryEntries({ path: { organizationId, venueId }, query: { limit: 100 } }),
    enabled: pickGame,
    staleTime: 60_000,
  });
  const gameOptions = [
    { value: '', label: t('sessions.start.gameNone') },
    ...(library.data?.data ?? [])
      .filter((entry) => entry.visible)
      .map((entry) => ({ value: entry.id, label: entry.game?.name ?? entry.customApp?.name ?? entry.id })),
  ];

  const minutes = minutesOf(time, useDeferredValue(custom));
  const quote = useConsoleQuote(
    organizationId,
    venueId,
    minutes === undefined ? null : { stationId: station.stationId, booking: { controllers, minutes }, ...(player === null ? {} : { playerId: player.id }) },
  );

  /** The catalogue's words for the code; a player already on another station is said in the staff's terms. */
  function refusal(mutationError: unknown): string {
    if (problemCode(mutationError) === PROBLEM.alreadyInSession) return t('sessions.start.alreadyInSession');
    return problem.text(mutationError, { fallback: t('consoleSession.start.refused') });
  }

  function submit(event: FormEvent<HTMLFormElement>): void {
    event.preventDefault();
    setError(undefined);
    setFieldError(undefined);
    if (player === null && !walkIn) {
      setError(t('consoleSession.start.pickPlayer'));
      return;
    }
    const booked = minutesOf(time, custom);
    if (booked === undefined) {
      setFieldError(t('consoleSession.start.minutesRequired'));
      return;
    }
    const label = station.name ?? t('floor.tile.numbered', { number: station.number });
    start.mutate(
      {
        stationId: station.stationId,
        booking: { controllers, minutes: booked },
        ...(player === null ? {} : { playerId: player.id }),
        ...(pickGame && game !== '' ? { libraryEntryId: game } : {}),
      },
      {
        onSuccess: () => {
          show({ tone: 'ok', title: t('consoleSession.start.done', { station: label, player: player === null ? t('consoleSession.walkIn') : playerName(player) }) });
          onDone();
        },
        onError: (mutationError) => setError(refusal(mutationError)),
      },
    );
  }

  return (
    <form className="console-session__form console-booking" aria-label={t('consoleSession.start.title')} noValidate onSubmit={submit}>
      {error === undefined ? null : (
        <Notice tone="danger" role="alert">
          {error}
        </Notice>
      )}
      {walkIn ? null : <PlayerPicker organizationId={organizationId} player={player} onPick={setPlayer} label={t('consoleSession.start.player')} />}
      {player === null ? (
        <div className="console-booking__field">
          <Checkbox label={t('consoleSession.start.walkIn')} checked={walkIn} onCheckedChange={(checked) => setWalkIn(checked === true)} />
          {walkIn ? <p className="console-subtle">{t('consoleSession.start.walkInHint')}</p> : null}
        </div>
      ) : null}
      <Chips
        label={t('consoleSession.start.controllers')}
        options={controllerChoices(station.capacity).map((count) => ({ value: count, label: String(count) }))}
        value={controllers}
        onChange={setControllers}
      />
      <Chips label={t('consoleSession.start.time')} options={timeOptions(true, t)} value={time} onChange={setTime} />
      {time === 'custom' ? (
        <TextField
          label={t('consoleSession.start.customMinutes')}
          description={t('consoleSession.start.customHint')}
          required
          numeric
          inputMode="numeric"
          value={custom}
          onChange={(e) => setCustom(e.target.value)}
          error={fieldError}
        />
      ) : null}
      {time === 'open' ? <p className="console-subtle">{t('consoleSession.start.openHint')}</p> : null}
      {pickGame ? <SelectField label={t('sessions.start.game')} description={t('sessions.start.gameHint')} options={gameOptions} value={game} onValueChange={setGame} /> : null}
      <PreviewBox loading={quote.isFetching && quote.data === undefined} failed={quote.isError} quote={quote.data} />
      <div className="console-form__actions">
        <Button type="submit" variant="primary" size="sm" busy={start.isPending}>
          {t('consoleSession.start.submit')}
        </Button>
        <Button type="button" variant="ghost" size="sm" onClick={onDone}>
          {t('sessions.actions.cancel')}
        </Button>
      </div>
    </form>
  );
}

function PreviewBox({ loading, failed, quote }: { readonly loading: boolean; readonly failed: boolean; readonly quote: ConsoleQuote | undefined }): ReactNode {
  const { t } = useTranslation('console');
  if (failed) return <p className="console-subtle">{t('consoleSession.preview.failed')}</p>;
  if (loading || quote === undefined) return <Skeleton shape="text" lines={2} />;
  return <QuoteLines quote={quote} title={t('consoleSession.preview.title')} footer={<p className="console-subtle">{t('consoleSession.preview.paidAtCounter')}</p>} />;
}

/** Add one more block to a running console session, with what it adds to what is due at the end. */
export function ConsoleAddTimeForm({ organizationId, session, onDone }: { readonly organizationId: string; readonly session: Session; readonly onDone: () => void }): ReactNode {
  const { t } = useTranslation('console');
  const problem = useProblem();
  const { show } = useToast();
  const add = useAddSessionTime(organizationId, session.id);
  const [time, setTime] = useState<TimeChoice>('60');
  const [custom, setCustom] = useState('');
  const [error, setError] = useState<string | undefined>(undefined);
  const minutes = minutesOf(time, useDeferredValue(custom));
  const open = session.booking?.open === true;
  const quote = useSessionChangeQuote(organizationId, session.id, open || minutes === undefined || minutes === null ? null : { addMinutes: minutes });

  if (open) return <Notice tone="info">{t('consoleSession.addTime.open')}</Notice>;

  function submit(event: FormEvent<HTMLFormElement>): void {
    event.preventDefault();
    setError(undefined);
    const booked = minutesOf(time, custom);
    if (booked === undefined || booked === null) {
      setError(t('consoleSession.start.minutesRequired'));
      return;
    }
    add.mutate(
      { minutes: booked },
      {
        onSuccess: () => {
          show({ tone: 'ok', title: t('consoleSession.addTime.done', { block: blockText(booked, t) }) });
          onDone();
        },
        onError: (failure) => setError(problem.text(failure, { fallback: t('consoleSession.addTime.failed') })),
      },
    );
  }

  return (
    <form className="console-session__form console-booking" aria-label={t('consoleSession.addTime.title')} noValidate onSubmit={submit}>
      <Chips label={t('consoleSession.start.time')} options={timeOptions(false, t)} value={time} onChange={setTime} />
      {time === 'custom' ? (
        <TextField label={t('consoleSession.start.customMinutes')} description={t('consoleSession.start.customHint')} required numeric inputMode="numeric" value={custom} onChange={(e) => setCustom(e.target.value)} />
      ) : null}
      {error === undefined ? null : (
        <Notice tone="danger" role="alert">
          {error}
        </Notice>
      )}
      {quote.data === undefined ? (
        quote.isError ? <p className="console-subtle">{t('consoleSession.preview.failed')}</p> : <Skeleton shape="text" lines={1} />
      ) : (
        <p className="console-booking__total">
          <span>{t('consoleSession.addTime.adds')}</span>
          <strong className="console-money">+{formatMoney(quote.data.difference)}</strong>
        </p>
      )}
      {quote.data === undefined ? null : (
        <p className="console-subtle">{t('consoleSession.addTime.dueAfter', { amount: formatMoney(quote.data.next.total) })}</p>
      )}
      <div className="console-form__actions">
        <Button type="submit" variant="primary" size="sm" busy={add.isPending}>
          {minutes === undefined || minutes === null ? t('consoleSession.addTime.title') : t('consoleSession.addTime.submit', { block: blockText(minutes, t) })}
        </Button>
        <Button type="button" variant="ghost" size="sm" onClick={onDone}>
          {t('sessions.actions.cancel')}
        </Button>
      </div>
    </form>
  );
}

/** Change the controllers of a running console session from this moment on, with what it changes. */
export function ConsoleControllersForm({
  organizationId,
  session,
  capacity,
  onDone,
}: {
  readonly organizationId: string;
  readonly session: Session;
  readonly capacity: number;
  readonly onDone: () => void;
}): ReactNode {
  const { t } = useTranslation('console');
  const problem = useProblem();
  const { show } = useToast();
  const set = useSetSessionControllers(organizationId, session.id);
  const current = session.booking?.controllers ?? 1;
  const [controllers, setControllers] = useState(current);
  const [error, setError] = useState<string | undefined>(undefined);
  const quote = useSessionChangeQuote(organizationId, session.id, controllers === current ? null : { controllers });

  function submit(event: FormEvent<HTMLFormElement>): void {
    event.preventDefault();
    setError(undefined);
    if (controllers === current) {
      onDone();
      return;
    }
    set.mutate(
      { controllers },
      {
        onSuccess: () => {
          show({ tone: 'ok', title: t('consoleSession.controllers.done', { count: controllers }) });
          onDone();
        },
        onError: (failure) => setError(problem.text(failure, { fallback: t('consoleSession.controllers.failed') })),
      },
    );
  }

  return (
    <form className="console-session__form console-booking" aria-label={t('consoleSession.controllers.title')} noValidate onSubmit={submit}>
      <Chips label={t('consoleSession.start.controllers')} options={controllerChoices(capacity).map((count) => ({ value: count, label: String(count) }))} value={controllers} onChange={setControllers} />
      <p className="console-subtle">{t('consoleSession.controllers.fromNow')}</p>
      {error === undefined ? null : (
        <Notice tone="danger" role="alert">
          {error}
        </Notice>
      )}
      {controllers === current || quote.data === undefined ? null : (
        <p className="console-booking__total">
          <span>{t('consoleSession.controllers.changes')}</span>
          <strong className="console-money">
            {quote.data.difference.amount < 0 ? '−' : '+'}
            {formatMoney({ amount: Math.abs(quote.data.difference.amount), currency: quote.data.difference.currency })}
          </strong>
        </p>
      )}
      <div className="console-form__actions">
        <Button type="submit" variant="primary" size="sm" busy={set.isPending}>
          {t('consoleSession.controllers.submit', { count: controllers })}
        </Button>
        <Button type="button" variant="ghost" size="sm" onClick={onDone}>
          {t('sessions.actions.cancel')}
        </Button>
      </div>
    </form>
  );
}

/** A running (or ended) console session's booking: blocks, controllers, and what is due at the end. */
export function ConsoleDue({ session }: { readonly session: Session }): ReactNode {
  const { t } = useTranslation('console');
  const booking = session.booking;
  if (booking === undefined) return null;
  const booked = booking.open
    ? t('consoleSession.due.open')
    : booking.blocks.map((block) => (block.minutes === null ? t('consoleSession.start.open') : blockText(block.minutes, t))).join(' + ');
  return (
    <div className="console-booking console-booking--due">
      <p className="console-booking__summary">
        <span>{t('consoleSession.due.booked', { time: booked })}</span>
        <span className="console-subtle"> · {t('consoleSession.due.controllers', { count: booking.controllers })}</span>
        {session.walkIn === true ? <span className="console-subtle"> · {t('consoleSession.walkIn')}</span> : null}
      </p>
      <QuoteLines
        quote={booking.quote}
        footer={
          <p className="console-subtle">{booking.saleId === null ? t('consoleSession.preview.paidAtCounter') : t('consoleSession.due.ordered')}</p>
        }
      />
    </div>
  );
}
